import { zodResolver } from '@hookform/resolvers/zod';
import {
  Button,
  FileUploader,
  Form as FormContext,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  MoneyInput,
  Textarea,
} from '@kalos/ui';
import { type FC, useMemo } from 'react';
import { type SubmitHandler, useForm, useWatch } from 'react-hook-form';

import { JobSelector } from '../../../components/JobSelector';
import { type MostLikelyJobRequestFilter } from '../../../hooks/react-query/useEventsQuery';
import { timestamp } from '../../../tools/helpers';
import { TechniciansComboboxPicker } from '../../ComponentsLibrary/Pickers/newPickers/TechnicianComboboxPicker';
import {
  reimbursementCreateFormSchema,
  type ReimbursementCreateFormValues,
} from './reimbursementFormSchemas';

type ReimbursementEditFormSchemaType = ReimbursementCreateFormValues;

// Form component
export const ReimbursementCreateForm: FC<{
  onSave: (arg: { data: ReimbursementEditFormSchemaType }) => void;
  defaultValues: ReimbursementEditFormSchemaType;
  isLoading?: boolean;
  disabled?: boolean;
  disableTechnicianPicker?: boolean;
}> = ({ onSave, disableTechnicianPicker, disabled, defaultValues, isLoading }) => {
  const form = useForm<ReimbursementEditFormSchemaType>({
    resolver: zodResolver(reimbursementCreateFormSchema),
    mode: 'onChange',
    defaultValues,
    disabled,
  });

  const userId = useWatch({ control: form.control, name: 'userId' });

  const hint = useMemo<MostLikelyJobRequestFilter>(() => {
    return {
      userId,
      targetDate: timestamp(false, new Date()),
    };
  }, [userId]);

  const handleSave: SubmitHandler<ReimbursementEditFormSchemaType> = async (data) => {
    onSave({ data });
  };

  return (
    <FormContext {...form}>
      <form
        onSubmit={form.handleSubmit(handleSave)}
        className="@sm:grid-cols-2 grid grid-cols-1 gap-4 overflow-auto sm:gap-2 md:gap-4"
      >
        <FormField
          control={form.control}
          name="amount"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Amount</FormLabel>
              <FormMessage />
              <FormControl>
                <MoneyInput {...field} />
              </FormControl>
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="jobNumber"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Job Number</FormLabel>
              <FormMessage />
              <FormControl>
                <JobSelector
                  hint={hint}
                  onChange={field.onChange}
                  onBlur={field.onBlur}
                  value={field.value}
                />
              </FormControl>
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="description"
          render={({ field }) => (
            <FormItem className="col-span-1 sm:col-span-2">
              <FormLabel>Description</FormLabel>
              <FormMessage />
              <FormControl>
                <Textarea
                  {...field}
                  onChange={(e) => {
                    field.onChange(e);
                    form.trigger('description');
                  }}
                />
              </FormControl>
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="photo"
          render={({ field }) => (
            <FormItem className="col-span-1 sm:col-span-2">
              <div className="flex items-center gap-2">
                <FormLabel>Photo</FormLabel>
                <FormMessage />
              </div>
              <FormControl>
                <FileUploader
                  value={field.value}
                  onValueChange={field.onChange}
                  accept={{
                    'image/*': [],
                    'application/pdf': [],
                  }}
                  disabled={disabled || field.disabled}
                />
              </FormControl>
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="userId"
          render={({ field }) => (
            <FormItem className="@sm:col-span-2">
              <FormLabel>Reimbursement for:</FormLabel>
              <FormMessage />
              <FormControl>
                <TechniciansComboboxPicker
                  disabled={field.disabled || disableTechnicianPicker}
                  selected={field.value.toString()}
                  onSelect={(u) => field.onChange(u?.id ?? 0)}
                />
              </FormControl>
            </FormItem>
          )}
        />
        <div className="@sm:col-span-2 flex w-full gap-4 justify-self-end sm:w-auto">
          <Button
            type="submit"
            isLoading={isLoading}
            className="flex-1 sm:flex-auto"
            disabled={disabled}
          >
            Save
          </Button>
        </div>
      </form>
    </FormContext>
  );
};
